import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  /** Number of local digits expected (excluding dial code) */
  digitCount: number;
}

const countries: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', digitCount: 10 },
  { code: 'CA', name: 'Canada', dialCode: '+1', digitCount: 10 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', digitCount: 10 },
  { code: 'AU', name: 'Australia', dialCode: '+61', digitCount: 9 },
  { code: 'IN', name: 'India', dialCode: '+91', digitCount: 10 },
  { code: 'DE', name: 'Germany', dialCode: '+49', digitCount: 10 },
  { code: 'FR', name: 'France', dialCode: '+33', digitCount: 9 },
  { code: 'JP', name: 'Japan', dialCode: '+81', digitCount: 10 },
  { code: 'CN', name: 'China', dialCode: '+86', digitCount: 11 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', digitCount: 11 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', digitCount: 10 },
  { code: 'ES', name: 'Spain', dialCode: '+34', digitCount: 9 },
  { code: 'IT', name: 'Italy', dialCode: '+39', digitCount: 10 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', digitCount: 9 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', digitCount: 9 },
];

/** Format phone digits based on country. US/CA → (XXX) XXX-XXXX, others → groups of 3-4 */
function formatPhoneNumber(digits: string, country: Country): string {
  const d = digits.slice(0, country.digitCount);
  if (country.code === 'US' || country.code === 'CA') {
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  // Generic: groups of 3 separated by spaces
  return d.replace(/(\d{3})(?=\d)/g, '$1 ');
}

interface PhoneNumberInputProps {
  countryCode?: string;
  phoneNumber?: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (number: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function PhoneNumberInput({
  countryCode = '+1',
  phoneNumber = '',
  onCountryCodeChange,
  onPhoneNumberChange,
  error,
  disabled = false,
}: PhoneNumberInputProps) {
  const { t } = useTranslation('auth');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = countries.find((c) => c.dialCode === countryCode && c.code === (countryCode === '+1' ? (countries.find(x => x.dialCode === countryCode)?.code ?? 'US') : countries.find(x => x.dialCode === countryCode)?.code)) 
    ?? countries.find((c) => c.dialCode === countryCode) 
    ?? countries[0];

  const filtered = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search) ||
      c.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (c: Country) => {
    onCountryCodeChange(c.dialCode);
    onPhoneNumberChange('');
    setIsOpen(false);
    setSearch('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, selected.digitCount);
    onPhoneNumberChange(digits);
  };

  const displayValue = formatPhoneNumber(phoneNumber, selected);

  const digits = (phoneNumber || '').replace(/\D/g, '');
  const isIncomplete = digits.length > 0 && digits.length !== selected.digitCount;
  const displayError = error || (isIncomplete ? t('phoneInput.invalidDigits', { count: selected.digitCount }) : undefined);

  // Placeholder changes per country
  const placeholder = selected.code === 'US' || selected.code === 'CA'
    ? '(555) 000-0000'
    : selected.code === 'IN'
    ? '98765 43210'
    : '000 000 0000';

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        {/* Country Selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            className={cn(
              'flex items-center gap-1.5 h-11 px-3 rounded-xl border bg-white transition-all text-sm',
              'hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              displayError ? 'border-red-500' : 'border-slate-200'
            )}
          >
            <span className="font-medium text-slate-700 min-w-[2rem]">{selected.code}</span>
            <span className="text-slate-500">{selected.dialCode}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>

          {isOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
              <div className="absolute top-full left-0 mt-1.5 w-72 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="p-2 border-b border-slate-100">
                  <input
                    type="text"
                    placeholder={t('phoneInput.searchCountry')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 text-slate-900 placeholder:text-slate-400"
                    autoFocus
                  />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {filtered.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => handleSelect(c)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors',
                        selected.code === c.code && 'bg-slate-50 font-medium'
                      )}
                    >
                      <span className="font-medium text-slate-500 w-8">{c.code}</span>
                      <span className="flex-1 text-slate-700">{c.name}</span>
                      <span className="text-slate-400 text-xs">{c.dialCode}</span>
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <p className="px-4 py-6 text-center text-sm text-slate-400">{t('phoneInput.noResults')}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Phone Input */}
        <input
          type="tel"
          value={displayValue}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex-1 h-11 rounded-xl border bg-white px-4 py-2 text-sm transition-all',
            'placeholder:text-slate-400 text-slate-900',
            'focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900',
            'disabled:cursor-not-allowed disabled:opacity-50',
            displayError ? 'border-red-500 focus:ring-red-500/20' : 'border-slate-200 hover:border-slate-300'
          )}
        />
      </div>

      {displayError && <p className="text-xs text-red-500 px-1">{displayError}</p>}
    </div>
  );
}
